import type { PlainDateString } from "@sa-nu/shared-utils/date";
import { parsePlainDateString } from "@sa-nu/shared-utils/date";

import type { DatePickerDateInfo } from "../../../../../_components/dx/ui/date-picker/date-picker";
import { UNAVAILABLE_PRICE_LABEL } from "../../../_shared/price-labels";

/**
 * Drawer のフェーズ
 * - idle: CI 未選択 (在庫なし夜は disabled)
 * - selectingEnd: CI 選択済み、CO 選択中 (coDisabledDates が disabled)
 * - confirmed: CI/CO 両方選択済み (在庫なし夜は disabled = 新 CI に選べない)
 */
export type CheckoutPickerPhase = "idle" | "selectingEnd" | "confirmed";

type BuildParams = {
  phase: CheckoutPickerPhase;
  /** guaranteedPrice が存在する日付 → フォーマット済み金額文字列 */
  priceMap: Record<PlainDateString, string>;
  /** phase=idle / confirmed 時に在庫なしで disabled にする日付 */
  unavailableDates?: Set<PlainDateString>;
  /** phase=selectingEnd 時に disabled にするチェックアウト不可日 */
  coDisabledDates?: Set<PlainDateString>;
  /**
   * phase=confirmed 時、この日付の price を UNAVAILABLE_PRICE_LABEL に上書きする。
   * DatePicker の endDate に相当する日 = チェックアウト日で、宿泊料金が発生しない日を明示するため。
   */
  endDate?: PlainDateString;
};

/**
 * DatePicker に渡す dateInfoMap を合成する。
 *
 * - price は常に表示 (在庫なし夜は priceMap 側で "-" になっている)
 * - disabled の付与条件はフェーズに依存:
 *   - idle / confirmed: unavailableDates に含まれる日付。見た目は通常のまま
 *     非インタラクティブ (disabledAppearance: "normal") にし、"-" 表示で在庫なしを伝える。
 *     confirmed でも disable しないと在庫なし日が新 CI として選べてしまう
 *   - selectingEnd: coDisabledDates に含まれる日付 (従来どおり muted)。
 *     窓内の在庫なし日が CO に選べるのは backend 公認のオフバイワンのため、
 *     unavailableDates はこのフェーズでは使わない
 */
export function buildCheckoutDateInfoMap({
  phase,
  priceMap,
  unavailableDates,
  coDisabledDates,
  endDate,
}: BuildParams): Record<PlainDateString, DatePickerDateInfo> {
  const allDates = new Set<PlainDateString>(
    Object.keys(priceMap).map((k) => parsePlainDateString(k)),
  );

  if (phase !== "selectingEnd" && unavailableDates) {
    unavailableDates.forEach((d) => allDates.add(d));
  }
  if (phase === "selectingEnd" && coDisabledDates) {
    coDisabledDates.forEach((d) => allDates.add(d));
  }

  return [...allDates].reduce<Record<PlainDateString, DatePickerDateInfo>>(
    (acc, date) => {
      const price = resolvePrice({ phase, date, priceMap, endDate });
      const disabled = resolveDisabled(phase, date, {
        unavailableDates,
        coDisabledDates,
      });
      const info: DatePickerDateInfo = {};
      if (price !== undefined) info.price = price;
      if (disabled) {
        info.disabled = true;
        // idle / confirmed の在庫なし夜は muted せず通常表示のまま非インタラクティブにする
        if (phase !== "selectingEnd") info.disabledAppearance = "normal";
      }
      acc[date] = info;
      return acc;
    },
    {},
  );
}

function resolvePrice({
  phase,
  date,
  priceMap,
  endDate,
}: {
  phase: CheckoutPickerPhase;
  date: PlainDateString;
  priceMap: Record<PlainDateString, string>;
  endDate: PlainDateString | undefined;
}): string | undefined {
  // confirmed 時のチェックアウト日は宿泊料金が発生しないため、priceMap の値に関わらず「-」で明示する
  if (phase === "confirmed" && endDate !== undefined && date === endDate) {
    return UNAVAILABLE_PRICE_LABEL;
  }
  return priceMap[date];
}

function resolveDisabled(
  phase: CheckoutPickerPhase,
  date: PlainDateString,
  {
    unavailableDates,
    coDisabledDates,
  }: {
    unavailableDates?: Set<PlainDateString>;
    coDisabledDates?: Set<PlainDateString>;
  },
): boolean {
  if (phase === "selectingEnd") {
    return coDisabledDates?.has(date) ?? false;
  }
  // idle / confirmed: 在庫なし夜は CI に選べない
  return unavailableDates?.has(date) ?? false;
}
